import type { messages as ptMessages } from './pt-BR';

export const messages: Record<keyof typeof ptMessages, string> = {
  'teams.badges.subtree': 'Subtree: {count}',
  'teams.collapse': 'Collapse {name}',
  'teams.expand': 'Expand {name}',
  'teams.subteams': 'Sub-teams of {name}',
  'teams.loading': 'Loading structure...',
  'teams.error': 'Failed to load campaign structure.',
  'teams.empty.title': 'No structure',
  'teams.empty.description': 'No teams or levels registered for this campaign.',
  'teams.title': 'Teams',

  // Equipes screen (Teams.tsx)
  'teams.nav.label': 'Teams',
  'teams.view.cards': 'Cards',
  'teams.view.tree': 'Tree',
  'teams.list.levels.label': 'Campaign levels',
  'teams.list.empty.title': 'No teams yet',
  'teams.list.empty.description': 'A team appears here as soon as one is created under a level.',
  'teams.accounts.one': '{count} account',
  'teams.accounts.other': '{count} accounts',

  // Cards tab (Teams.tsx, TeamCard.tsx, LevelSummary.tsx)
  'teams.cards.label': 'Team hierarchy',
  'teams.unassigned.link.one': '{count} account with no team',
  'teams.unassigned.link.other': '{count} accounts with no team',
  'teams.card.lead.label': 'Lead',
  'teams.card.figure.members': 'Members',
  'teams.card.figure.reach': 'Reach',
  'teams.card.figure.posts': 'Posts',
  'teams.card.figure.sentiment': 'Sentiment change',
  'teams.card.lead.hint': 'who answers for this team',
  'teams.card.figure.members.hint': 'tracked accounts in this team and the teams inside it',
  'teams.card.figure.reach.hint': 'no data source yet, so it shows empty',
  'teams.card.figure.posts.hint': 'posts by the accounts in this team and the teams inside it',
  'teams.card.figure.sentiment.hint':
    'support minus hostility in comments over the last 7 days, against the 7 before',
  'teams.card.accounts.label': 'Accounts of {name}',
  'teams.card.accounts.loading': 'Loading accounts...',
  'teams.card.accounts.error': 'Failed to load accounts.',

  // Blocked state (NoStructure.tsx)
  'teams.blocked.title': 'Organise accounts by team',
  'teams.blocked.description':
    'See reach, posts and sentiment totalled by management, region or community.',

  // Structure presets (StructurePresets.tsx; Figma 329:12603)
  'teams.presets.groupLabel': 'Structure preset',
  'teams.presets.action': 'Use this structure',
  'teams.presets.scratch.title': 'Start from scratch',
  'teams.presets.scratch.description': 'You define each level',
  'teams.presets.levelCount.one': '{count} level',
  'teams.presets.levelCount.other': '{count} levels',
  'teams.presets.level.manager': 'Manager',
  'teams.presets.level.regional': 'Regional',
  'teams.presets.level.community': 'Community',
  'teams.presets.level.coordination': 'Coordination',
  'teams.presets.level.neighborhood': 'Neighbourhood',

  // Structure dialog (StructureDialog.tsx, Structure.tsx; Figma 325:11801, 678:20157)
  'teams.editor.trigger': 'Structure',
  'teams.editor.title': 'Team structure',
  'teams.editor.addLevel': 'Add level',
  'teams.editor.level.name.label': 'Level name',
  'teams.editor.removeLevel': 'Remove level',
  'teams.editor.undoRemoveLevel': 'Undo',
  'teams.editor.level.teamCount.one': '{count} team',
  'teams.editor.level.teamCount.other': '{count} teams',
  'teams.editor.save': 'Save structure',
  'teams.editor.saving': 'Saving…',
  'teams.editor.saved': 'Structure saved',
  'teams.editor.save.failed': 'Could not save. Try again.',
  'teams.editor.consequence.label': 'On save:',
  'teams.editor.consequence.teamsRemoved.one': '{count} team stops existing.',
  'teams.editor.consequence.teamsRemoved.other': '{count} teams stop existing.',
  'teams.editor.consequence.accountsMoved.one': '{count} account moves up a level.',
  'teams.editor.consequence.accountsMoved.other': '{count} accounts move up a level.',
  'teams.editor.consequence.accountsUnassigned.one': '{count} account is left without a team.',
  'teams.editor.consequence.accountsUnassigned.other': '{count} accounts are left without a team.',

  // New-team dialog (NewTeam.tsx, ParentPicker.tsx, LeadPicker.tsx)
  'teams.newTeam.title': 'New team',
  'teams.newTeam.name.label': 'Team name',
  'teams.newTeam.name.required': 'Enter the team name',
  'teams.newTeam.submit': 'Create team',

  'teams.newTeam.parent.label': 'Inside',
  'teams.newTeam.parent.placeholder': 'Choose where the team goes',
  'teams.newTeam.parent.required': 'Choose where the team goes.',
  'teams.newTeam.parent.searchPlaceholder': 'Search team',
  'teams.newTeam.parent.empty': 'No team found',
  'teams.newTeam.parent.top': 'No team above',
  'teams.newTeam.parent.topGroup': 'At the top · creates {level}',
  'teams.newTeam.parent.group': '{level} · creates {next}',
  'teams.newTeam.parent.derivedLevel': 'Creates a team at level',

  'teams.newTeam.lead.label': 'Lead',
  'teams.newTeam.lead.placeholder': 'Search for a lead',
  'teams.newTeam.lead.searchPlaceholder': 'Search lead by name or @',
  'teams.newTeam.lead.group': 'Monitored accounts',
  'teams.newTeam.lead.empty': 'No monitored account with that name.',
  'teams.newTeam.lead.useNameOnly': 'Use "{name}" with no monitored account',
  'teams.newTeam.lead.remove': 'Remove lead "{name}"',

  // Team "⋯" menu (TeamActions.tsx)
  'teams.actions.label': 'Actions for {name}',
  'teams.actions.newTeamIn': 'Add team inside {name}',
  'teams.actions.edit': 'Edit team',
  'teams.actions.accounts': 'Add account',
  'teams.actions.remove': 'Remove team',
  'teams.remove.title': 'Remove {name}?',
  'teams.remove.description':
    'This team’s accounts are left with no team. A team with teams inside it cannot be removed.',
  'teams.remove.confirm': 'Remove team',

  // Edit-team dialog (EditTeam.tsx)
  'teams.editTeam.title': 'Edit team',
  'teams.editTeam.submit': 'Save',
  'teams.editTeam.level.label': 'Level',
  'teams.editTeam.moveSubtree.label': 'Goes along to {name}:',
  'teams.editTeam.moveSubtree.teams.one': '{count} team',
  'teams.editTeam.moveSubtree.teams.other': '{count} teams',
  'teams.editTeam.failed': 'Could not save. Try again.',

  // Team accounts dialog (TeamAccounts.tsx)
  'teams.accountsDialog.title': 'Accounts in {name}',
  'teams.accountsDialog.searchPlaceholder': 'Search account',
  'teams.accountsDialog.searchEmpty': 'No account found.',
  'teams.accountsDialog.elsewhere': 'in {team}',
  'teams.accountsDialog.elsewhereUnknown': 'another team',
  'teams.accountsDialog.unassigned': 'no team',
  'teams.accountsDialog.count.one': '{count} account in this team',
  'teams.accountsDialog.count.other': '{count} accounts in this team',
  'teams.accountsDialog.empty': 'No accounts in this team yet.',
  'teams.accountsDialog.remove': 'Remove {name} from the team',
  'teams.accountsDialog.moveFailed': 'Could not move. Try again.',
  'teams.accountsDialog.done': 'Done',
};
